import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { ToastController, ModalController, LoadingController, AlertController, NavController } from '@ionic/angular';
import { Store, Select } from '@ngxs/store';
import { UtilitiesService } from '../services/utilities.service';
import { AddOtherSettings, AddFacilitySetting, AddAllSettings } from '../data/actions/settings.action';
import { LogoutModalComponent } from '../components/utilities/utilities-modals/logout-modal/logout-modal.component';
import { AuthConnectProvider } from './account/auth-connect';
import { IOtherSetting, IFacilitySetting } from '../models/service/other-settings.model';
import { environment } from 'src/environments/environment';
import { EnvironmentData, EnvironmentSetting } from 'src/environments/environment-data';
import * as Sentry from '@sentry/capacitor';
import { init as sentryAngularInit, browserTracingIntegration } from '@sentry/angular-ivy';
import { SettingsState, SettingsStateI } from '../data/state/settings.state';
import { Observable, BehaviorSubject } from 'rxjs';
import { UnitsReducer } from './units/units.reducer';
import { AddExceptionList } from '../data/actions/checkpoint.action';
import { RegionType } from '../enums/region-type.enum';
import { ModeType } from 'src/app/enums/mode-type.enum';

@Injectable({
  providedIn: 'root',
})
export class UtilitiesProvider {
  private loadingCount = 0;
  private loadingView: any;
  private loadingTimeout: any;
  toast: HTMLIonToastElement;
  public facilitySettings: IFacilitySetting;
  public selectedFacility$ = new BehaviorSubject<string>('');
  public tabsBarCanUpdate$ = new BehaviorSubject<boolean>(false);
  private PHIShownFlag: boolean;
  private projectName = 'tray-logistics';

  @Select(SettingsState.getSettingsState)
  otherSettings$: Observable<SettingsStateI>;

  constructor(
    private toastController: ToastController,
    private loadingCtrl: LoadingController,
    private modalController: ModalController,
    private alertController: AlertController,
    private store: Store,
    private utilitiesService: UtilitiesService,
    private unitReducer: UnitsReducer,
    private authProvider: AuthConnectProvider,
    private navCtrl: NavController
  ) {
    this.otherSettings$.subscribe((settingsState) => {
      this.facilitySettings = settingsState.facilitySettings;
    });
  }

  async presentToast(header?: string, message?: string, overrideOptions?: any): Promise<void> {
    try {
      if (this.toast) {
        this.toast.dismiss();
      }
    } catch (error) {
      console.error('Toast error', error);
    }
    const toastOptions = {
      header,
      message,
      position: 'top',
      duration: 5000,
      cssClass: 'ion-text-center',
      buttons: [
        {
          icon: 'close-outline',
          role: 'cancel',
        },
      ],
    };
    const options = Object.assign(toastOptions, overrideOptions);
    this.toast = await this.toastController.create(options);
    this.toast.present();
  }

  async presentLoadingViewWithMessage(message: string, addClasses = ''): Promise<any> {
    const loading = await this.loadingCtrl.create({
      spinner: 'crescent',
      duration: 20000,
      message,
      translucent: true,
      cssClass: addClasses,
      backdropDismiss: false,
    });
    return loading;
  }

  killLoading(): void {
    if (this.loadingView) {
      this.loadingCount = 0;
      this.loadingView.dismiss();
      this.clearTimer();
    }
  }

  async presentModal(component: any, componentProps?: any, cssClass?: string): Promise<HTMLIonModalElement> {
    const modal: HTMLIonModalElement = await this.modalController.create({
      component,
      componentProps,
      cssClass,
    });
    return modal;
  }

  private clearTimer() {
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }
  }

  async presentAlertMessage(
    custClass: string,
    custHeader: string,
    custSubHeader: string,
    custMessage: string,
    custButton: any[]
  ): Promise<any> {
    const alertMessage = await this.alertController.create({
      cssClass: custClass,
      header: custHeader,
      subHeader: custSubHeader,
      message: custMessage,
      buttons: custButton,
    });
    return alertMessage;
  }

  public static GetColorHex(colorCode: number): string {
    let hexColor = colorCode.toString(16);
    if (hexColor.length > 6) {
      // just in case of Pink = 1BFCFFF and Orange = 10080FF colors
      if (hexColor.toUpperCase() === '1BFCFFF' || hexColor.toUpperCase() === '10080FF') {
        hexColor = hexColor.padStart(7, '000000');
        const blueColor = hexColor.substr(1, 2);
        const greenColor = hexColor.substr(3, 2);
        const redColor = hexColor.substr(5, 2);
        return redColor + greenColor + blueColor;
      }
      return null;
    }
    hexColor = hexColor.padStart(6, '000000');
    const blue = hexColor.substr(0, 2);
    const green = hexColor.substr(2, 2);
    const red = hexColor.substr(4, 2);
    return red + green + blue;
  }

  public getTimeZone(): string {
    return this.facilitySettings.timeZoneId;
  }

  public getLocalTimeZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  public getTextColor(backgroundColor: string): string {
    // STEP 1: convert hex to RGB array
    const rgbArr = backgroundColor
      ?.replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i, (m, r, g, b) => '#' + r + r + g + g + b + b)
      .substring(1)
      .match(/.{2}/g)
      .map((x) => parseInt(x, 16));
    const yiq = (rgbArr[0] * 299 + rgbArr[1] * 587 + rgbArr[2] * 114) / 1000;
    return yiq >= 128 ? 'rgba(0,0,0,0.87)' : 'white';
  }

  public async getAllSettings(): Promise<void> {
    const loadingView = await this.presentLoadingViewWithMessage('Loading general settings... ');
    loadingView.present();
    const otherSettings = this.getOtherSettings();
    const userIsSSO = await this.authProvider.userIsSSO();
    if (userIsSSO) this.getAllSettingsFromMW();
    if (!!otherSettings) {
      const meals = await this.unitReducer
        .getMeals()
        .then(() => true)
        .catch(() => null);
      if (!!meals) {
        await this.getAndSaveExceptionList();
        await this.getFacility();
      }
    }
    await this.wait(0.5);
    loadingView.dismiss();
  }

  private async getAllSettingsFromMW(): Promise<void> {
    await this.utilitiesService
      .getAllSettings()
      .then((allSettings) => this.store.dispatch(new AddAllSettings(allSettings)));
  }

  public async getOtherSettings(): Promise<IOtherSetting[]> {
    const otherSettings = await this.utilitiesService.getOtherSettings().then(
      (res) => res,
      () => null
    );
    this.PHIShownFlag = this.isPHIShown(otherSettings);
    this.store.dispatch(new AddOtherSettings(otherSettings ?? []));
    return otherSettings;
  }

  public async getFacility(): Promise<IFacilitySetting> {
    const facility: IFacilitySetting = await this.utilitiesService.getFacility().catch(() => null);
    const version = await this.utilitiesService
      .getMiddlewareVersion()
      .then((res) => res)
      .catch(() => null);
    if (!!facility) {
      if (!!version) {
        facility.version = version.version;
      }
      this.store.dispatch(new AddFacilitySetting(facility));
    }
    return facility;
  }

  public async getAndSaveExceptionList(): Promise<any> {
    const exceptionListRes = await this.utilitiesService
      .getExceptionList()
      .then((res) => res)
      .catch(() => null);
    if (!!exceptionListRes) {
      if (exceptionListRes.data.length > 0) {
        this.store.dispatch(new AddExceptionList(exceptionListRes.data));
      } else {
        this.store.dispatch(new AddExceptionList([]));
      }
    } else {
      this.store.dispatch(new AddExceptionList([]));
    }
  }

  public static getRefresherTimer(settings: IOtherSetting[]): number {
    const refreshTraysSetting = settings.find((setting) => setting.name === 'TLRefreshTrayDataSeconds');
    return (refreshTraysSetting?.value ? Number(refreshTraysSetting.value) : 20) * 1000;
  }

  public static deepCopy(obj: any): any {
    let copy: any;

    // Handle the 3 simple types, and null or undefined
    if (null == obj || 'object' !== typeof obj) {
      return obj;
    }

    // Handle Date
    if (obj instanceof Date) {
      copy = new Date();
      copy.setTime(obj.getTime());
      return copy;
    }

    // Handle Array
    if (obj instanceof Array) {
      copy = [];
      for (let i = 0, len = obj.length; i < len; i++) {
        copy[i] = this.deepCopy(obj[i]);
      }
      return copy;
    }

    // Handle Object
    if (obj instanceof Object) {
      copy = {};
      for (const attr in obj) {
        // eslint-disable-next-line no-prototype-builtins
        if (obj.hasOwnProperty(attr)) {
          copy[attr] = this.deepCopy(obj[attr]);
        }
      }
      return copy;
    }

    throw new Error(`Unable to copy obj! Its type isn't supported.`);
  }

  public async wait(seconds: number): Promise<void> {
    await new Promise((resolve) => {
      setTimeout(resolve, seconds * 1000);
    });
  }

  // Pass this peace of code to logout-modal.component
  async logOut(): Promise<void> {
    const {
      allSettings: { logoutUrl },
    } = this.store.selectSnapshot(SettingsState.getSettingsState);
    const userIsSSO = await this.authProvider.userIsSSO();
    if (logoutUrl === undefined && userIsSSO) await this.getAllSettingsFromMW();
    const view = await this.modalController.create({
      component: LogoutModalComponent,
      cssClass: 'modal-general-css',
    });
    view.present();
    const { data } = await view.onDidDismiss();
    if (data?.logout) {
      await this.logoutAuditedReport(AuthConnectProvider.getSettings().organizationId);
      this.logoutUser().then(() => {
        this.navCtrl.navigateRoot('/login');
      });
    }
  }

  public async logoutUser(): Promise<void> {
    const { appClientId, region } = AuthConnectProvider.getSettings();
    const href = AuthConnectProvider.cleanUrlLogin();
    const userIsSSO = await this.authProvider.userIsSSO();
    let {
      allSettings: { logoutUrl },
    } = this.store.selectSnapshot(SettingsState.getSettingsState);

    if (!logoutUrl || !userIsSSO) logoutUrl = href;

    this.authProvider.configureAuthConnect(appClientId, href, logoutUrl, region, 'CURRENT');
    await this.authProvider.logoutUser(true);
  }

  public getCurrentPlatform(): boolean {
    if (
      Capacitor.getPlatform() === 'ios' ||
      (Capacitor.getPlatform() === 'android' &&
        (Capacitor.getPlatform() !== 'desktop' || Capacitor.getPlatform() === 'mobileweb'))
    ) {
      //native
      return false;
    } else {
      //browser
      return true;
    }
  }

  getInitialEnvironmentSettingsSSM(region: string = RegionType.USA): EnvironmentSetting[] {
    try {
      if (EnvironmentData.verifyEnvironment()) {
        return EnvironmentData.getCurrentEnvs();
      } else {
        EnvironmentData.setEnvironmentSwitcher(environment.mode);

        // when is Prod and Staging the settings should pull up from env files
        const localSSMValue = EnvironmentData.getLocalSSM(environment.mode, region);
        return localSSMValue ? [localSSMValue] : environment.configurationData;
      }
    } catch (error) {
      console.log(error);
    }
  }

  public async logoutAuditedReport(organizationId: string): Promise<string> {
    try {
      const logoutAuditedRep = await this.utilitiesService.logoutAudited(organizationId);
      return logoutAuditedRep;
    } catch (error) {
      console.error(error);
    }
  }

  private isPHIShown(otherSettings: IOtherSetting[] | null): boolean {
    if (!otherSettings.length) return false;

    const showPHIVaule = otherSettings.find(({ name }) => name === 'TLUnitViewShowPHI');

    return showPHIVaule.value === 'Y';
  }

  public getPhiFlag(): boolean {
    return this.PHIShownFlag;
  }

  public initiateSentry() {
    const currentEnv = Object.keys(ModeType).filter((key) => ModeType[key] == environment.mode);
    const sentryInitOptions = {
      dsn: 'https://0abfe199e74214f6140223fb4a9101a3@o4506734803025920.ingest.us.sentry.io/4507307348852736',
      release: this.projectName + '@' + EnvironmentData.version.versionNumber,
      dist: '1',
      tracesSampleRate: 1.0,
      environment: currentEnv[0],
      integrations: [browserTracingIntegration()],
      denyUrls: [/localhost(:\d+)?\/?|127\.0\.0\.1(:\d+)?\/?/],
    };
    Sentry.init(sentryInitOptions, sentryAngularInit);
  }
}
